<script lang="ts">
	import { browser } from '$app/environment';
	import { afterNavigate } from '$app/navigation';
	import { page } from '$app/state';
	import Button from '$lib/Button.svelte';
	import ScrollProgressBar from '$lib/ScrollProgressBar.svelte';
	import SiteSearch from '$lib/SiteSearch.svelte';
	import beansproutLogo from '$lib/assets/beansprout-logo.svg';
	import { userStore } from '$lib/stores/user';
	import { pathMatch } from '$lib/utils';
	import { signOut } from 'aws-amplify/auth';
	import { CircleUserRound, Mail, Menu, Search, Star, X } from 'lucide-svelte';
	import NavDesktop from './NavDesktop.svelte';
	import NavMobile from './NavMobile.svelte';
	import SearchMobile from './SearchMobile.svelte';

	interface Props {
		tools: Populated<BaseTool, never>[];
	}

	let { tools }: Props = $props();

	let menuOpen = $state(false);
	let searchOpen = $state(false);

	let navItems = [
		{
			label: 'Insights',
			children: [
				{ label: 'Latest', url: '/insights' },
				{ label: 'Trending', url: '/insights/trending' },
				{ label: 'CPF', url: '/insights/cpf' },
				{ label: 'Bonds', url: '/insights/bonds' },
				{ label: 'Singapore Depository Receipts', url: '/insights/singapore-depository-receipts' },
				{ label: 'REITs', url: '/insights/reits' },
				{ label: 'ETFs', url: '/insights/etfs' },
				{ label: 'Private Wealth', url: '/insights/alternatives' },
				{ label: 'Savings', url: '/insights/savings' },
				{ label: 'Stocks', url: '/insights/stocks' },
				{ label: 'Mutual Funds', url: '/insights/mutual-funds' }
			]
		},
		{
			label: 'Reviews',
			children: [
				{ label: 'Latest', url: '/reviews' },
				{ label: 'Brokerage Account', url: '/reviews/brokerage-account' },
				{ label: 'Cash Management', url: '/reviews/cash-management' },
				{ label: 'Robo Advisor', url: '/reviews/robo-advisor' },
				{ label: 'Savings Account', url: '/reviews/savings-account' }
			]
		},
		{
			label: 'Markets',
			children: [
				{ label: 'Singapore securities', url: '/securities' },
				{ label: 'Singapore REITs', url: '/reits' },
				{ label: 'Singapore Mutual Funds', url: '/mutual-funds' }
			]
		},
		{
			label: 'Tools',
			children: [
				...tools.map((tool) => ({
					label: tool.name,
					url: tool.url
				})),
				{ label: 'View All Tools', url: '/tools', exact: true }
			]
		},
		{
			label: 'Investing 101',
			children: [
				{ label: 'Latest', url: '/investing-101' },
				{ label: 'ETFs', url: '/investing-101/etfs-101' },
				{ label: 'Mutual Funds', url: '/investing-101/mutual-funds-101' },
				{ label: 'Stocks', url: '/investing-101/stocks-101' }
			]
		},
		{ label: 'Events', url: '/events' },
		{
			label: 'About',
			children: [
				{ label: 'Company', url: '/about-us' },
				{
					label: 'Partners',
					url: '/partners'
				}
			]
		},
		{ label: 'Promo', url: '/promo' }
	];

	$effect(() => {
		if (browser) {
			document.body.style.overflow = menuOpen || searchOpen ? 'hidden' : 'auto';
		}
	});

	afterNavigate(() => {
		menuOpen = false;
		searchOpen = false;
	});
</script>

<div class:sticky={!page.data.disableStickyHeader} class="top-0 z-30 w-full">
	<header class="border-b-gray-light bg-white-pure relative border-b-2">
		<div class="page-wrapper">
			<div class="mx-auto flex h-14 max-w-screen-xl items-center justify-between">
				<a href="/" class="mr-8 block shrink-0">
					<img src={beansproutLogo} alt="Beansprout logo" class="aspect-[175/37] w-32 lg:w-40" />
				</a>

				<div class="flex justify-between gap-2 lg:grow">
					{#if !(menuOpen || searchOpen) && !$userStore.signedIn}
						<Button size="small" class="lg:hidden" to="/newsletter" data-type="header-subscribe"
							>Subscribe</Button
						>
					{/if}

					<div class="flex min-w-0 items-center justify-end lg:grow">
						<div class="hidden w-full max-w-md lg:block">
							<SiteSearch />
						</div>
						{#if !menuOpen}
							<button
								id="search-open"
								onclick={() => (searchOpen = !searchOpen)}
								aria-label={searchOpen ? 'Close' : 'Search'}
								class="px-1 lg:hidden"
							>
								{#if searchOpen}
									<X class="w-6" />
								{:else}
									<Search class="w-6" />
								{/if}
							</button>
						{/if}

						{#if $userStore.signedIn}
							<div class="group relative hidden lg:ml-8 lg:flex">
								<CircleUserRound class="mr-2 w-6" />
								{$userStore.nickname}
								<ul
									class="bg-white-pure absolute top-full right-0 z-10 hidden gap-2 overflow-visible rounded-2xl p-4 whitespace-nowrap drop-shadow group-hover:grid"
								>
									<li>
										<a href="/watchlist" class="index-bold flex h-full items-center">
											<Star class="mr-2 w-4" />
											My Watchlist
										</a>
									</li>
									<li>
										<button
											onclick={async () => {
												await signOut();
												location.href = '';
											}}
											class="index-regular text-gray-dark">Sign out</button
										>
									</li>
								</ul>
							</div>
						{:else}
							<Button class="hidden lg:ml-8 lg:block" to="/newsletter" data-type="header-subscribe"
								>Subscribe</Button
							>
							<a href="/signup" class="index-regular ml-8 hidden lg:block">Create free account</a>
						{/if}
					</div>
					{#if !searchOpen}
						<button
							id={menuOpen ? 'menu-close' : 'menu-open'}
							class="text-2xl lg:hidden"
							onclick={() => (menuOpen = !menuOpen)}
							aria-label="menu"
						>
							{#if menuOpen}
								<X class="w-6" />
							{:else}
								<Menu class="w-6" />
							{/if}
						</button>
					{/if}
				</div>
			</div>
			<nav
				aria-label="main"
				class="m-auto hidden h-14 w-full max-w-screen-xl items-center justify-between gap-8 text-sm lg:flex"
			>
				<NavDesktop {navItems} />
				<a
					class:active={pathMatch(page.url.pathname, '/contact')}
					href="/contact/"
					class="contact-link"
				>
					<Mail class="w-4" />
					Contact</a
				>
			</nav>
		</div>
		<ScrollProgressBar />
	</header>
</div>

{#if menuOpen}
	<NavMobile {navItems} bind:menuOpen />
{/if}
{#if searchOpen}
	<SearchMobile />
{/if}

<style lang="postcss">
	@reference '../../app.css';

	.contact-link {
		@apply text-gray-mid relative inline-flex items-center gap-1;

		&.active {
			@apply text-green-primary font-medium;
		}

		&:after {
			content: '';
			@apply absolute bottom-0 left-0 h-[2px] w-full rounded;
		}
		&:hover {
			&:after {
				@apply bg-gray-mid;
			}
		}
	}
</style>
